/*
 * Eseley "Notify me when back in stock" — Klaviyo back-in-stock + optional list/SMS subscribe.
 *
 * Loaded as a <script type="module"> from snippets/ese-back-in-stock.liquid, only when a
 * <ese-back-in-stock> element can actually render. Zero network activity on page view: every
 * fetch here happens inside submit(), which only runs when a shopper clicks/taps the submit
 * button or presses Enter in a form field.
 *
 * Consent model (Derek, 2026-09-13): the back-in-stock EMAIL alert is transactional — a valid
 * email is all it takes to send it, no consent checkbox gates it. The marketing checkbox (only
 * rendered when a Klaviyo list id is set) is OPTIONAL and controls only whether the shopper is
 * also added to that marketing list. SMS is the one channel that still needs its own explicit,
 * ticked consent box, ported from Carlyle Avenue's back-in-stock implementation
 * (assets/ca-back-in-stock.js, assets/consent.js, snippets/ca-back-in-stock.liquid) with fixes —
 * see _consentOk() below. Because nothing here depends on Shopify's Customer Privacy API anymore
 * (there's no more privacy-signal gate to satisfy), this file does NOT load or query it: no
 * consentApiReady, no marketingConsentAllowed, no navigator.globalPrivacyControl read. One less
 * network-adjacent thing to await, and nothing left that would have depended on it.
 *
 * D2 (Derek 2026-09-13): SMS consent never satisfies email gating; email alert is transactional.
 *
 * No klaviyo.js, no setTrackingConsent/klaviyo globals/_learnq, no cookies or Web Storage, no
 * injected scripts. No PII in dispatched events. Never logs the phone number.
 */
(function () {
  if (customElements.get('ese-back-in-stock')) return;
  // No <dialog> support: leave the element :not(:defined), which the Liquid-side CSS takeover
  // already hides, so Prestige's native "Sold out" button shows instead of a broken fallback UI.
  if (typeof HTMLDialogElement === 'undefined') return;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const CONTROL_CHAR_RE = /[\x00-\x1f\x7f]/;
  const FOCUSABLE_SELECTOR = 'button, [href], input, [tabindex]:not([tabindex="-1"])';
  const MIN_DWELL_MS = 1500;
  const MAX_SENDS_PER_PAGE = 3;
  const PENDING_FOCUS_TTL_MS = 1000;
  // Shape checks run before ANY fetch. The values come from theme settings (merchant-editable), so
  // a malformed one fails closed instead of being interpolated into a request header/body.
  const REVISION_RE = /^\d{4}-\d{2}-\d{2}$/;
  const LIST_ID_RE = /^[A-Za-z0-9]{4,10}$/;
  // The consent-copy version lives in the Liquid, next to the copy itself, as data-copy-version on
  // the root element (so it is bumped in the same diff as the text). It is recorded in Klaviyo's
  // custom_source so each consent can be traced to the copy the shopper saw. Missing or malformed
  // fails closed before any fetch.
  const COPY_VERSION_RE = /^\d{4}-\d{2}-\d{2}$/;

  // Module-level state, intentionally NOT persisted anywhere (no storage, no cookies).
  let pendingFocusFormId = null;
  let pendingFocusTimer = null;
  let sendCount = 0;

  function setPendingFocus(formId) {
    pendingFocusFormId = formId;
    clearTimeout(pendingFocusTimer);
    // Safety net: if no replacement element connects (the new variant is in stock) and no
    // variant:change reaches us, don't leave a stale id that could steal focus much later.
    pendingFocusTimer = setTimeout(clearPendingFocus, PENDING_FOCUS_TTL_MS);
  }

  function clearPendingFocus() {
    pendingFocusFormId = null;
    clearTimeout(pendingFocusTimer);
    pendingFocusTimer = null;
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/[^\w-]/g, '\\$&');
  }

  /** Focus the shopper's checked variant radio (used when the trigger we would normally return
   * focus to is gone or about to be removed by a variant swap). Leaves focus alone if it's already
   * on one of the form's option inputs. Returns true when something was focused or already was. */
  function focusCheckedVariantRadio(formId) {
    if (!formId) return false;
    const base = 'input[data-option-position][form="' + cssEscape(formId) + '"]';
    const active = document.activeElement;
    if (active && active.matches && active.matches(base)) return true;
    const radio = document.querySelector(base + ':checked');
    if (!radio) return false;
    radio.focus();
    return true;
  }

  /** One persistent, visually hidden role="status" live region per form id. It lives next to the
   * product form, OUTSIDE <buy-buttons> (which Prestige swaps on every variant change), so it is
   * never replaced and screen readers keep a stable region to announce into. Created once, reused
   * if present. Inline styles: this node is created by JS and isn't tied to a section-scoped class. */
  function ensureLiveRegion(formId) {
    if (!formId) return null;
    const regionId = formId + '-bis-live';
    let region = document.getElementById(regionId);
    if (region) return region;
    const form = document.forms[formId];
    if (!form) return null;
    region = document.createElement('div');
    region.id = regionId;
    region.setAttribute('role', 'status');
    region.setAttribute('data-ebis-live', '');
    region.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    const anchor = form.closest('product-form') || form;
    anchor.insertAdjacentElement('afterend', region);
    return region;
  }

  function announce(formId, message) {
    const region = ensureLiveRegion(formId);
    if (!region || !message) return;
    region.textContent = '';
    // Write on a later task so the empty->text change is seen as a new announcement even when the
    // region was created a moment ago or the same text repeats.
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  }

  // Delegated, page-level: variant:change bubbles from the product form. Prestige swaps
  // <buy-buttons> BEFORE it dispatches this event, so by now a replacement element (if the new
  // variant is also sold out) is already connected, and any old, listener-aborted instance is gone.
  document.addEventListener('variant:change', (event) => {
    const detail = event.detail || {};
    // Do NOT use detail.formId or form.id: Prestige dispatches `formId: form.id`, and in this theme
    // the product form has a named control <input name="id">, which clobbers the HTMLFormElement
    // `id` property (it returns that input element, not the string). Read the attribute instead,
    // from the form the event was dispatched on.
    const formEl = event.target && event.target.closest ? event.target.closest('form') : null;
    const formId = formEl ? formEl.getAttribute('id') : null;
    // m1: a pending "focus the trigger" that nothing consumed (no replacement element connected)
    // would otherwise leak into a much later connect. Hand focus to the checked variant radio so it
    // doesn't fall to <body>, then drop the pending state.
    if (pendingFocusFormId !== null) {
      if (pendingFocusFormId === formId) focusCheckedVariantRadio(formId);
      clearPendingFocus();
    }
    // M2: announce a switch to a sold-out variant when Notify me is actually shown for it.
    const variant = detail.variant;
    if (!variant || variant.available !== false || !formId) return;
    const shown = Array.prototype.find.call(document.querySelectorAll('ese-back-in-stock'), (el) => {
      return el.dataset.formId === formId && !el.hidden && String(el.dataset.variantId) === String(variant.id);
    });
    if (!shown || !shown.dataset.announce) return;
    const triggerEl = shown.querySelector('[data-ebis-open]');
    // textContent only (never innerHTML); tokens are replaced as plain strings.
    announce(
      formId,
      shown.dataset.announce
        .split('[variant]')
        .join(variant.title || '')
        .split('[button]')
        .join(triggerEl ? triggerEl.textContent.trim() : '')
    );
  });

  // Establish the live region(s) early, while the page is quiet, so the first announcement lands in
  // a region the screen reader already knows about (even when the initial variant is in stock and no
  // <ese-back-in-stock> is rendered yet).
  document.querySelectorAll('buy-buttons[form*="product-form-main-"]').forEach((el) => {
    ensureLiveRegion(el.getAttribute('form'));
  });

  /** POST to the Klaviyo public client API. No PII beyond what the caller puts in `body`. */
  function klaviyoPost(resource, company, revision, body, signal) {
    return fetch('https://a.klaviyo.com/client/' + resource + '/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: revision },
      body: JSON.stringify(body),
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      mode: 'cors',
      cache: 'no-store',
      signal: signal,
    });
  }

  function postBackInStock(company, revision, variantId, channels, profileAttributes, signal) {
    return klaviyoPost(
      'back-in-stock-subscriptions',
      company,
      revision,
      {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: channels,
            profile: { data: { type: 'profile', attributes: profileAttributes } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } },
          },
        },
      },
      signal
    );
  }

  /** Marketing-list subscribe. `subscriptions` is an explicit per-channel object containing ONLY
   * the channels the shopper ticked (email and/or sms marketing); the profile attributes carry only
   * the matching identifiers. */
  function postListSubscribe(company, revision, listId, profileAttributes, subscriptions, customSource, signal) {
    return klaviyoPost(
      'subscriptions',
      company,
      revision,
      {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: Object.assign({}, profileAttributes, { subscriptions: subscriptions }),
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      },
      signal
    );
  }

  /** Classify a non-2xx Klaviyo response. Never surfaces Klaviyo's raw detail text to the
   * shopper or the console — only status/code get logged, and only booleans derived from the body
   * are returned. */
  async function classifyBisError(response) {
    let code = '';
    let pointerMentionsVariant = false;
    let detailMentionsEmail = false;
    let mentionsPhoneOrSms = false;
    try {
      const json = await response.json();
      const err = json && json.errors && json.errors[0];
      code = (err && err.code) || '';
      const pointer = (err && err.source && err.source.pointer) || '';
      const detail = (err && err.detail) || '';
      pointerMentionsVariant = /variant/i.test(pointer) || /variant/i.test(detail);
      detailMentionsEmail = /email/i.test(detail) || /email/i.test(pointer);
      mentionsPhoneOrSms = /phone|sms/i.test(pointer) || /phone|sms/i.test(detail);
    } catch (_parseErr) {
      // Body wasn't JSON (or was empty) — fall through with the defaults above.
    }
    return {
      code: code,
      pointerMentionsVariant: pointerMentionsVariant,
      detailMentionsEmail: detailMentionsEmail,
      mentionsPhoneOrSms: mentionsPhoneOrSms,
    };
  }

  // Adapted from Carlyle Avenue's assets/consent.js (normalizePhone, lines 54-62). US numbers ONLY,
  // to match the US-only TCPA consent copy: the result is always +1 followed by 10 digits, anything
  // else (any other country code, wrong length) returns null and surfaces the existing phone error.
  // Best-effort shape check; Klaviyo does the authoritative validation.
  function normalizePhone(raw) {
    const trimmed = (raw || '').trim();
    if (!trimmed) return null;
    const digits = trimmed.replace(/\D/g, '');
    if (trimmed.charAt(0) === '+') {
      // An explicit country code must be +1; never reinterpret "+44..." as a 10-digit US number.
      return digits.length === 11 && digits[0] === '1' ? '+' + digits : null;
    }
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    return null;
  }

  class EseBackInStock extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this._busy = false;
      this._openedAt = 0;
      // Only initialize once: if this instance is ever disconnected and reconnected without being
      // garbage-collected, resetting the counter back to 0 could let a stale, already-cancelled
      // submit's captured token coincidentally match again.
      if (this._submitToken == null) this._submitToken = 0;
      this._controller = new AbortController();
      this._fetchController = null;
      this._bisController = null;

      this._dialog = this.querySelector('[data-ebis-dialog]');
      this._trigger = this.querySelector('[data-ebis-open]');
      this._formView = this.querySelector('[data-ebis-form-view]');
      this._successView = this.querySelector('[data-ebis-success-view]');
      this._emailInput = this.querySelector('[data-ebis-email]');
      this._honeypot = this.querySelector('[data-ebis-honeypot]');
      this._marketingCheckbox = this.querySelector('[data-ebis-marketing]');
      this._phoneInput = this.querySelector('[data-ebis-phone]');
      this._smsConsent = this.querySelector('[data-ebis-sms-consent]');
      this._smsConsentWrap = this.querySelector('[data-ebis-sms-consent-wrap]');
      this._submitBtn = this.querySelector('[data-ebis-submit]');
      this._errorEl = this.querySelector('[data-ebis-error]');
      this._listNote = this.querySelector('[data-ebis-list-note]');
      this._smsFailedNote = this.querySelector('[data-ebis-sms-failed-note]');
      this._smsAlertFailedNote = this.querySelector('[data-ebis-sms-alert-failed-note]');
      this._statusEl = this.querySelector('[data-ebis-status]');
      this._successHeading = this.querySelector('[data-ebis-success-heading]');
      // The dialog is labelled by the form heading; the success view swaps it to the success
      // heading (m3) and _reset() swaps it back.
      this._formLabelledBy = this._dialog ? this._dialog.getAttribute('aria-labelledby') : null;
      this._focusVariantRadioOnClose = false;
      // Via _setBusy (after the refs above exist) so readonly / aria-disabled / aria-busy are
      // cleared on a reconnect too, not just the flag.
      this._setBusy(false);

      if (!this._dialog || !this._trigger || !this._emailInput || !this._submitBtn) return;

      const signal = this._controller.signal;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('keydown', this._onKeydown.bind(this), { signal });
      this.addEventListener('input', this._onInput.bind(this), { signal });
      this.addEventListener('change', this._onChange.bind(this), { signal });

      this._dialog.addEventListener('close', this._onDialogClose.bind(this), { signal });

      const form = document.forms[this.dataset.formId];
      this._form = form || null;
      if (form) {
        form.addEventListener('variant:change', this._onVariantChange.bind(this), { signal });
      }

      // M2: make sure this form's persistent live region exists (outside <buy-buttons>).
      ensureLiveRegion(this.dataset.formId);

      if (pendingFocusFormId === this.dataset.formId) {
        clearPendingFocus();
        this._trigger.focus();
      }
    }

    disconnectedCallback() {
      if (this._dialog && this._dialog.open) {
        setPendingFocus(this.dataset.formId);
        document.documentElement.removeAttribute('data-ebs-scroll-lock');
      }
      this._submitToken += 1;
      if (this._controller) this._controller.abort();
      if (this._fetchController) this._fetchController.abort();
      // Unlike a dialog close, an actual disconnect (element removed — e.g. a variant swap
      // replacing <buy-buttons>) does cancel an in-flight back-in-stock request; there's no
      // element left to report back to.
      if (this._bisController) this._bisController.abort();
      this._wired = false;
    }

    /** True only while a specific submit() call is still the current one, connected, and the
     * dialog is still open — the gate every post-await checkpoint in submit() must pass before
     * touching the UI or dispatching an event. */
    _isCurrent(token) {
      return token === this._submitToken && this.isConnected && !!(this._dialog && this._dialog.open);
    }

    _onClick(event) {
      // M1: inputs aren't `disabled` while busy (that would drop focus), so checkbox toggles are
      // swallowed here instead. preventDefault on the click reverts the toggle, and also covers the
      // label-click and Space-key paths, which both synthesize a click on the input.
      if (this._busy && event.target.matches && event.target.matches('input[type="checkbox"]')) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('[data-ebis-open]')) {
        this.open();
        return;
      }
      if (event.target.closest('[data-ebis-close]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-ebis-submit]')) {
        this.submit();
        return;
      }
      // Backdrop click: the native <dialog> itself is the click target only when the click lands
      // outside the dialog's own box (the box is a child of <dialog>'s rendering, but a click
      // inside the visible card hits a descendant element instead).
      if (event.target === this._dialog) {
        const rect = this._dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) this.close();
      }
    }

    _onKeydown(event) {
      if (!this._dialog.open) return;
      if (event.isComposing) return;

      if (
        event.key === 'Enter' &&
        (event.target === this._emailInput ||
          event.target === this._marketingCheckbox ||
          event.target === this._phoneInput ||
          event.target === this._smsConsent)
      ) {
        event.preventDefault();
        this.submit();
        return;
      }

      if (event.key === 'Tab') {
        const focusables = Array.prototype.slice
          .call(this._dialog.querySelectorAll(FOCUSABLE_SELECTOR))
          .filter((el) => !el.disabled && el.getClientRects().length && el !== this._honeypot);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    _onInput(event) {
      // Text inputs are readonly while busy, so this is defense in depth (e.g. autofill).
      if (this._busy) return;
      if (event.target === this._emailInput) {
        this._emailInput.removeAttribute('aria-invalid');
      }
      // Reveal the SMS consent box once a phone value is entered (Carlyle parity —
      // ca-back-in-stock.js toggleSmsDisclaimer(), lines 163-167). Pure DOM state: no network
      // call, no storage write, no event. Clearing the phone back out also unticks the box, so it
      // never reappears pre-ticked for whatever number the shopper types next.
      if (event.target === this._phoneInput && this._smsConsentWrap) {
        const hasPhone = this._phoneInput.value.trim().length > 0;
        this._smsConsentWrap.hidden = !hasPhone;
        if (!hasPhone && this._smsConsent) this._smsConsent.checked = false;
      }
      // As soon as the shopper starts fixing an invalid field, drop the error text (Carlyle
      // parity — ca-back-in-stock.js connectedCallback's delegated 'input' listener).
      this._clearError();
    }

    // Ticking a flagged checkbox clears the error immediately, without waiting for resubmit
    // (Carlyle parity — ca-back-in-stock.js connectedCallback's delegated 'change' listener).
    _onChange(event) {
      if (this._busy) return;
      if (event.target && event.target.matches && event.target.matches('input[type="checkbox"]')) {
        this._clearError();
      }
    }

    _onDialogClose() {
      document.documentElement.removeAttribute('data-ebs-scroll-lock');
      // Stop a submit() that's still working through its steps (waiting out the dwell timer, or
      // in the middle of the list-subscribe) so it doesn't go on to send the NEXT request after
      // the shopper has closed the dialog, and so a close/reopen/resubmit can't run a second flow
      // alongside it. Aborting _fetchController cancels the client side only: a list POST that has
      // already been transmitted can still be processed by Klaviyo, we just stop waiting for it.
      // Bumping the token additionally makes a submit() that is already past the dwell timer skip
      // its UI updates and events once it notices the token no longer matches. This does NOT touch
      // _bisController — once the back-in-stock request has actually been sent, closing the dialog
      // must not cancel it (see submit()).
      this._submitToken += 1;
      if (this._fetchController) {
        this._fetchController.abort();
        this._fetchController = null;
      }
      this._reset();
      this._returnFocus();
    }

    _onVariantChange(event) {
      const newVariant = event.detail && event.detail.variant;
      const newId = newVariant && newVariant.id;
      if (String(newId) !== String(this.dataset.variantId)) {
        if (this._dialog.open) {
          // The trigger is about to be hidden/removed, so the close handler must send focus to the
          // shopper's checked variant radio instead (m2). The 'close' event fires on a later task,
          // hence a flag rather than a direct call.
          this._focusVariantRadioOnClose = true;
          this._dialog.close();
        }
        this.hidden = true;
      }
    }

    _returnFocus() {
      if (this._focusVariantRadioOnClose || this.hidden) {
        this._focusVariantRadioOnClose = false;
        if (focusCheckedVariantRadio(this.dataset.formId)) return;
      }
      if (this.isConnected && this._trigger) {
        this._trigger.focus();
        return;
      }
      const formId = this.dataset.formId;
      const fallback = document.querySelector('ese-back-in-stock[data-form-id="' + formId + '"] [data-ebis-open]');
      if (fallback) fallback.focus();
    }

    _variantMismatch() {
      const form = this._form || document.forms[this.dataset.formId];
      if (!form) return false;
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return false;
      return String(idInput.value) !== String(this.dataset.variantId);
    }

    // The email alert needs no consent box at all (Derek, 2026-09-13). SMS is the only channel
    // with a gate left: a phone entered without a ticked SMS box, or an invalid phone with the
    // box ticked, blocks the send. Reads CURRENT state and is re-checked at every checkpoint in
    // submit() — the phone/checkbox are readonly / ignored once busy, but this stays defense in depth.
    //
    // Format checked BEFORE the tick (deliberate deviation from Carlyle, which checks the tick
    // first): a shopper who mistypes a number shouldn't be told to check a box before also being
    // told the number itself is invalid — fixing the number is the more useful first message.
    _consentOk() {
      if (this._phoneInput) {
        const raw = this._phoneInput.value.trim();
        if (raw !== '') {
          if (!normalizePhone(raw)) return 'phone';
          if (!(this._smsConsent && this._smsConsent.checked)) return 'sms';
        }
      }
      return null;
    }

    // Shared UI for a _consentOk() failure. Callers are responsible for confirming _isCurrent()
    // (or that busy/token/controller haven't been created yet) before calling this — it reveals
    // and focuses form controls, which must never happen inside an already-closed dialog.
    _handleConsentFailure(kind) {
      if (kind === 'sms') {
        // Autofill can populate the phone input without ever firing our 'input' listener, leaving
        // the SMS box's wrap hidden even though a phone is now present — unhide it before we try
        // to focus a checkbox that isn't focusable while hidden.
        if (this._smsConsentWrap) this._smsConsentWrap.hidden = false;
        if (this._smsConsent) {
          this._smsConsent.setAttribute('aria-invalid', 'true');
          this._smsConsent.focus();
        }
        this._setError(this.dataset.errSmsConsent);
        return;
      }
      // kind === 'phone'
      if (this._phoneInput) {
        this._phoneInput.setAttribute('aria-invalid', 'true');
        this._phoneInput.focus();
      }
      this._setError(this.dataset.errPhone);
    }

    // Read fresh, right before each POST that could carry it — the phone must never be sent
    // unless the SMS box is CURRENTLY ticked. Returns the normalized E.164 phone, or null if
    // there's no phone, the box isn't ticked, or normalization fails (that last case should
    // already have been caught by _consentOk()'s 'phone' branch at the relevant checkpoint).
    _currentSmsPhone() {
      if (!this._phoneInput || !this._smsConsent || !this._smsConsent.checked) return null;
      const raw = this._phoneInput.value.trim();
      if (raw === '') return null;
      return normalizePhone(raw);
    }

    // The error node is always present in the DOM with role="alert" (never a hidden attribute) so
    // a screen reader's alert live region is already established before we ever write to it; it's
    // hidden purely by :empty in CSS. Text-only mutation is what actually gets announced.
    _setError(message) {
      if (!this._errorEl) return;
      this._errorEl.textContent = message;
    }

    _clearError() {
      if (this._errorEl) this._errorEl.textContent = '';
      this.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    _focusError() {
      if (this._errorEl) this._errorEl.focus();
    }

    _setBusy(isBusy) {
      this._busy = isBusy;
      if (this._submitBtn) {
        if (isBusy) {
          this._submitBtn.setAttribute('aria-busy', 'true');
          this._submitBtn.classList.add('is-loading');
        } else {
          this._submitBtn.removeAttribute('aria-busy');
          this._submitBtn.classList.remove('is-loading');
        }
      }
      // Lock every input for the duration of a submit so the shopper can't change anything
      // mid-flight (a checkbox untick, or a phone edit, between checkpoints). NOT `disabled`:
      // disabling the focused control would drop focus to <body> (M1). Text inputs go `readonly` +
      // aria-disabled instead (focus stays put, the value still can't be edited); checkboxes can't
      // be readonly, so they get aria-disabled and _onClick/_onChange ignore them while _busy, and
      // submit() ignores re-entry while _busy. We also still re-read current state at every
      // checkpoint in submit() as defense in depth.
      const lock = (el, isTextInput) => {
        if (!el) return;
        if (isTextInput) el.readOnly = isBusy;
        if (isBusy) el.setAttribute('aria-disabled', 'true');
        else el.removeAttribute('aria-disabled');
      };
      lock(this._emailInput, true);
      lock(this._phoneInput, true);
      lock(this._marketingCheckbox, false);
      lock(this._smsConsent, false);
      // A short status announcement for AT users while the request is in flight (text comes from
      // the bis_sending_text setting); cleared as soon as it isn't.
      if (this._statusEl) this._statusEl.textContent = isBusy ? this.dataset.sending || 'Sending…' : '';
    }

    _refundSend() {
      if (sendCount > 0) sendCount -= 1;
    }

    _reset() {
      this._clearError();
      this._setBusy(false);
      if (this._emailInput) {
        this._emailInput.value = '';
        this._emailInput.removeAttribute('aria-invalid');
      }
      if (this._honeypot) this._honeypot.value = '';
      if (this._marketingCheckbox) this._marketingCheckbox.checked = false;
      if (this._phoneInput) {
        this._phoneInput.value = '';
        this._phoneInput.removeAttribute('aria-invalid');
      }
      if (this._smsConsent) this._smsConsent.checked = false;
      if (this._smsConsentWrap) this._smsConsentWrap.hidden = true;
      if (this._formView) this._formView.hidden = false;
      if (this._successView) this._successView.hidden = true;
      if (this._listNote) this._listNote.hidden = true;
      if (this._smsFailedNote) this._smsFailedNote.hidden = true;
      if (this._smsAlertFailedNote) this._smsAlertFailedNote.hidden = true;
      // m3: back to labelling the dialog with the form heading.
      if (this._dialog && this._formLabelledBy) this._dialog.setAttribute('aria-labelledby', this._formLabelledBy);
    }

    // The notes are independent and can show together:
    //   emailListFailed   - the marketing-list subscribe failed to record EMAIL marketing consent
    //                       (only meaningful when the marketing box was ticked).
    //   smsListFailed     - the SMS marketing list did NOT record the shopper's text consent (the
    //                       list call with their phone failed, or had to be retried without it).
    //   smsAlertFailed    - the list subscription with their phone SUCCEEDED, but the restock text
    //                       could not be set up on the back-in-stock call (it was retried email-only).
    //                       Never true together with smsListFailed, so the shopper is never told
    //                       "we couldn't sign you up" about a list call that returned success.
    _showSuccess(emailListFailed, smsListFailed, smsAlertFailed) {
      if (this._formView) this._formView.hidden = true;
      if (this._successView) {
        this._successView.hidden = false;
        // m3: the visible heading is now the success heading, so label the dialog with it.
        if (this._dialog && this._successHeading && this._successHeading.id) {
          this._dialog.setAttribute('aria-labelledby', this._successHeading.id);
        }
        this._successView.focus();
      }
      if (this._listNote) this._listNote.hidden = !emailListFailed;
      if (this._smsFailedNote) this._smsFailedNote.hidden = !smsListFailed;
      if (this._smsAlertFailedNote) this._smsAlertFailedNote.hidden = !(smsAlertFailed && !smsListFailed);
    }

    open() {
      this._reset();
      this._openedAt = performance.now();
      this._dialog.showModal();
      document.documentElement.setAttribute('data-ebs-scroll-lock', '');
      requestAnimationFrame(() => {
        if (this._emailInput) this._emailInput.focus();
      });
    }

    close() {
      if (this._dialog.open) this._dialog.close();
    }

    async submit() {
      if (this._busy) return;
      this._clearError();

      // Honeypot: a real shopper never fills this field. Treat a filled one as a bot and send
      // nothing to Klaviyo.
      if (this._honeypot && this._honeypot.value !== '') {
        this._showSuccess(false, false, false);
        return;
      }

      const email = this._emailInput.value.trim();
      if (
        email.length === 0 ||
        email.length > 254 ||
        CONTROL_CHAR_RE.test(email) ||
        !EMAIL_RE.test(email)
      ) {
        this._emailInput.setAttribute('aria-invalid', 'true');
        this._setError(this.dataset.errEmail);
        this._emailInput.focus();
        return;
      }
      this._emailInput.removeAttribute('aria-invalid');

      // Checkpoint 1 of 4 — nothing async yet, so it's safe to reveal/focus directly.
      let consentFailure = this._consentOk();
      if (consentFailure) {
        this._handleConsentFailure(consentFailure);
        return;
      }

      if (this._variantMismatch()) {
        // Fail closed (don't send), but keep the dialog open with visible feedback — closing here
        // would wipe the error via _reset() before the shopper ever saw it.
        this._setError(this.dataset.errGeneric);
        this._focusError();
        return;
      }

      if (window.Shopify && window.Shopify.designMode) {
        this._showSuccess(false, false, false);
        return;
      }

      const company = this.dataset.company;
      const variantId = this.dataset.variantId;
      if (!company || !variantId) {
        this._setError(this.dataset.errGeneric);
        this._focusError();
        return;
      }

      // Fail closed BEFORE any fetch if the merchant-editable API revision or list id is malformed.
      // The warning carries no values and no PII.
      if (
        !REVISION_RE.test(this.dataset.revision || '') ||
        (this.dataset.list && !LIST_ID_RE.test(this.dataset.list)) ||
        !COPY_VERSION_RE.test(this.dataset.copyVersion || '')
      ) {
        console.warn('[ese-back-in-stock] invalid Klaviyo revision, list id or copy version; request not sent');
        this._setError(this.dataset.errGeneric);
        this._focusError();
        return;
      }

      if (sendCount >= MAX_SENDS_PER_PAGE) {
        this._setError(this.dataset.errRate);
        this._focusError();
        return;
      }

      this._setBusy(true);

      // Each submit() gets its own token, and incrementing it here retires any previous call's
      // token too (there shouldn't be one, since _busy guards re-entry, but this keeps a single
      // source of truth). _onDialogClose and disconnectedCallback bump this same counter to
      // invalidate an in-flight call from the outside. The AbortController is the network-level
      // half of the same guard, created before the dwell wait (not after) so a cancel during the
      // wait can actually stop the request before it's sent. It also covers the list-subscribe
      // call below — before the back-in-stock request succeeds, closing cancels everything not
      // yet sent.
      const token = ++this._submitToken;
      const controller = new AbortController();
      this._fetchController = controller;

      // Minimum dwell time between open and submit, to blunt scripted/bot submissions. Never
      // surfaced as an error — we just wait out the remainder before sending.
      const elapsed = performance.now() - this._openedAt;
      if (elapsed < MIN_DWELL_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_DWELL_MS - elapsed));
      }

      // The shopper may have canceled, or the variant may have changed out from under us, while
      // we were waiting out the dwell timer above — bail out without sending anything or touching
      // the (possibly gone) UI. The explicit signal check is defense in depth alongside
      // _isCurrent(), in case this controller was aborted without the token having moved yet.
      // Token-guarded busy-clear: this continuation can still resolve after a NEWER submit() has
      // already started (and is legitimately busy) — an unconditional _setBusy(false) here would
      // wrongly clear ITS busy state instead of this stale one's.
      if (!this._isCurrent(token) || controller.signal.aborted) {
        if (token === this._submitToken) this._setBusy(false);
        return;
      }

      // Checkpoint 2 of 4 — right before the first network call.
      consentFailure = this._consentOk();
      if (consentFailure) {
        this._setBusy(false);
        this._handleConsentFailure(consentFailure);
        return;
      }

      const revision = this.dataset.revision;
      const listId = this.dataset.list;
      const sourceEmail = 'Back in stock v' + this.dataset.copyVersion;
      const sourceSms = 'Back in stock SMS TCPA v' + this.dataset.copyVersion;
      const optedIn = !!(this._marketingCheckbox && this._marketingCheckbox.checked);
      // Read fresh, right before building the list-subscribe payload — never inferred from an
      // earlier snapshot.
      const smsPhoneForList = this._currentSmsPhone();
      let listSubscribed = null;
      // True only when a list call that carried the shopper's phone number came back ok. This is
      // what "the SMS marketing list recorded the text consent" means; it is NOT implied by
      // listSubscribed, because an email-only retry (S2) can succeed without the phone.
      let listPhoneRecorded = false;
      // Either opt-in alone is enough to justify a list call now that email consent is no longer
      // required — SMS consent is recorded via this same endpoint, so a marketing-checkbox-off,
      // SMS-checkbox-on shopper must still reach it.
      const firstCallWasList = !!(listId && (optedIn || smsPhoneForList));

      // One slot per submit() flow, claimed before the first network attempt (list-subscribe when
      // one is warranted, otherwise back-in-stock) — closing repeatedly during a list POST can't
      // dodge the cap indefinitely. Refunded ONLY if the whole flow ends without ever reaching
      // Klaviyo: reachedKlaviyo flips true the moment ANY POST gets a response, of ANY status — a
      // 5xx still counts, because the flow goes on to attempt the next call regardless. Only a
      // network failure or an abort before any response is ever received keeps it false, in which
      // case the slot is given back.
      sendCount += 1;
      let reachedKlaviyo = false;

      // Error events carry only variant id, status and stage — no PII. A shopper's own cancel (the
      // dialog closed, so the controller is aborted) is not reported as an error.
      // `retried` is true on list errors that belong to a flow where the S2 email-only retry was
      // attempted, so a listener can tell the first failure (which the retry may recover from) from a
      // final one.
      const dispatchListError = (status, retried) => {
        if (this._isCurrent(token) && !controller.signal.aborted) {
          this.dispatchEvent(
            new CustomEvent('ese-back-in-stock:error', {
              bubbles: true,
              detail: { variantId: variantId, status: status, stage: 'list', retried: !!retried },
            })
          );
        }
      };

      // 1. List subscribe FIRST — Carlyle parity (ca-back-in-stock.js handleSubmit(), lines
      //    447-459), adapted: `email` is included only when the marketing box is ticked, and
      //    `phone_number` only when the SMS box is ticked and the phone normalized. Neither
      //    implies the other — an SMS-only tick sends a phone-only body with NO email field at
      //    all, so it can't subscribe the email address to marketing. The body also carries an
      //    explicit per-channel "subscriptions" object naming ONLY the channels that were ticked
      //    (email marketing and/or sms marketing), so channel consent is never inferred from which
      //    identifiers happen to be present. custom_source is versioned with the consent copy, and
      //    the call that carries a phone uses the SMS/TCPA source. Best-effort: a failure here must
      //    not lose the back-in-stock alert below, but it stays visible via the error event and the
      //    success view's notes, and it doesn't block step 2. Cancellable via the shared
      //    controller; aborting only stops us waiting — a request already transmitted can still be
      //    processed by Klaviyo.
      if (firstCallWasList) {
        const listProfile = {};
        const listChannels = {};
        if (optedIn) {
          listProfile.email = email;
          listChannels.email = { marketing: { consent: 'SUBSCRIBED' } };
        }
        if (smsPhoneForList) {
          listProfile.phone_number = smsPhoneForList;
          listChannels.sms = { marketing: { consent: 'SUBSCRIBED' } };
        }
        listSubscribed = false;
        try {
          const listResponse = await postListSubscribe(
            company,
            revision,
            listId,
            listProfile,
            listChannels,
            smsPhoneForList ? sourceSms : sourceEmail,
            controller.signal
          );
          reachedKlaviyo = true;
          if (listResponse.ok) {
            listSubscribed = true;
            listPhoneRecorded = !!smsPhoneForList;
          } else {
            // S2: the list call failed, it carried a phone, and the email-marketing box is ticked.
            // If Klaviyo's 4xx points at the phone / SMS side, retry once email-only so the shopper
            // still gets the email-marketing consent they asked for. Mirrors the back-in-stock
            // email-only retry below: 4xx only, never 429 or 5xx. The retry body is built from
            // scratch (email only, email channel only, email source) — it can't contain the phone.
            // The box state is re-read right now rather than trusting the earlier snapshot.
            let willRetryList = false;
            if (
              smsPhoneForList &&
              optedIn &&
              listResponse.status >= 400 &&
              listResponse.status < 500 &&
              listResponse.status !== 429
            ) {
              const listClassification = await classifyBisError(listResponse);
              willRetryList =
                listClassification.mentionsPhoneOrSms &&
                !!(this._marketingCheckbox && this._marketingCheckbox.checked) &&
                !controller.signal.aborted;
            }
            // Reported once the retry decision is known, with `retried` saying which way it went.
            dispatchListError(listResponse.status, willRetryList);
            if (willRetryList) {
              try {
                const retryResponse = await postListSubscribe(
                  company,
                  revision,
                  listId,
                  { email: email },
                  { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  sourceEmail,
                  controller.signal
                );
                if (retryResponse.ok) {
                  // Email marketing recorded; the phone was NOT (listPhoneRecorded stays false),
                  // and the success view says so.
                  listSubscribed = true;
                } else {
                  dispatchListError(retryResponse.status, true);
                }
              } catch (_listRetryErr) {
                dispatchListError(0, true);
              }
            }
          }
        } catch (_listErr) {
          dispatchListError(0);
        }
      }

      // The shopper may have closed the dialog (or the element may have been removed) while the
      // list call(s) above were in flight. The back-in-stock request has NOT been sent yet, so stop
      // here: closing cancels every request that hasn't been sent. A list request that was already
      // transmitted can't be recalled — its outcome is simply ignored.
      if (!this._isCurrent(token) || controller.signal.aborted) {
        if (token === this._submitToken) this._setBusy(false);
        if (!reachedKlaviyo) this._refundSend();
        return;
      }

      // Checkpoint 3 of 4 — right before the back-in-stock call itself. Stricter than Carlyle
      // (which has no equivalent re-check at all), intentionally.
      consentFailure = this._consentOk();
      if (consentFailure) {
        this._setBusy(false);
        if (!reachedKlaviyo) this._refundSend();
        this._handleConsentFailure(consentFailure);
        return;
      }

      // 2. ONE back-in-stock subscription. Runs on its OWN controller, never aborted by a dialog
      //    close (only disconnectedCallback aborts it) — once this fetch is sent, the shopper's
      //    actual ask has left; mirror "once it's left, let it finish". Never `keepalive`. Phone is
      //    read fresh again, right here, AND the restock SMS additionally requires that the list
      //    call RECORDED that exact phone (listPhoneRecorded): if the list call failed (4xx, 5xx,
      //    network) or only its email-only retry succeeded, no SMS consent was recorded, so the
      //    restock call goes out EMAIL-only with no phone_number at all.
      const bisController = new AbortController();
      this._bisController = bisController;
      const smsPhoneForBis =
        listPhoneRecorded && smsPhoneForList && this._currentSmsPhone() === smsPhoneForList ? smsPhoneForList : null;
      const bisChannels = smsPhoneForBis ? ['EMAIL', 'SMS'] : ['EMAIL'];
      const bisProfile = smsPhoneForBis ? { email: email, phone_number: smsPhoneForBis } : { email: email };
      // Reflects whether THIS back-in-stock call actually included SMS in its final, successful
      // attempt — true if included and never retried away, false if the SMS-inclusive attempt was
      // retried email-only, null if no phone was sent at all (none entered, or the list call did not
      // record it - see above).
      let smsSubscribed = smsPhoneForBis ? true : null;

      let response;
      try {
        response = await postBackInStock(company, revision, variantId, bisChannels, bisProfile, bisController.signal);
        reachedKlaviyo = true;
      } catch (err) {
        if (this._bisController === bisController) this._bisController = null;
        // Give the slot back only if the WHOLE flow (including a possible list call above) never
        // got a response from Klaviyo.
        if (!reachedKlaviyo) this._refundSend();
        // The only thing that aborts this controller is disconnectedCallback (the element was
        // removed, e.g. by a variant swap). That is not a failure and there is nothing left to
        // report to, so no error UI and no :error event.
        if (bisController.signal.aborted) return;
        if (this._isCurrent(token)) {
          this._setBusy(false);
          this._setError(this.dataset.errGeneric);
          this._focusError();
        }
        this.dispatchEvent(
          new CustomEvent('ese-back-in-stock:error', {
            bubbles: true,
            detail: { variantId: variantId, status: 0, stage: 'bis' },
          })
        );
        return;
      }
      if (this._bisController === bisController) this._bisController = null;

      let classification = null;
      if (!response.ok) {
        classification = await classifyBisError(response);

        // Retry ONLY on a 4xx whose pointer/detail mentions phone or SMS — never 429, never a 5xx,
        // never a network error or abort. Carlyle retries on any failure; this is deliberately
        // narrower. The retry behaves like the back-in-stock POST it replaces: gated on the
        // controller's own not-aborted signal, NOT on _isCurrent(token) — the shopper closing the
        // dialog must not cancel it (it carries strictly less than what was already sent: email
        // only, no phone), matching "once it's left, let it finish" for the original SMS-inclusive
        // attempt. UI updates below stay gated on _isCurrent(). `this.isConnected` IS still part of
        // this gate (unlike the dialog-open check): `_bisController` was cleared right after the
        // first response, before the `await classifyBisError(response)` above — a disconnect
        // during that read couldn't have aborted anything, so without this check the retry could
        // fire from an element already removed from the DOM with no way to cancel it.
        const shouldRetryEmailOnly =
          !!smsPhoneForBis &&
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 429 &&
          classification.mentionsPhoneOrSms;

        if (shouldRetryEmailOnly && this.isConnected && !bisController.signal.aborted) {
          smsSubscribed = false;
          // The first attempt's catch/success paths already cleared this — restore it so a
          // disconnect during the retry can still abort it.
          this._bisController = bisController;
          try {
            response = await postBackInStock(company, revision, variantId, ['EMAIL'], { email: email }, bisController.signal);
          } catch (_retryErr) {
            if (this._bisController === bisController) this._bisController = null;
            // The FIRST attempt already reached Klaviyo (reachedKlaviyo is already true), so no
            // refund here regardless of how the retry itself fails. A disconnect abort is not an
            // error (see the first attempt's catch).
            if (bisController.signal.aborted) return;
            if (this._isCurrent(token)) {
              this._setBusy(false);
              this._setError(this.dataset.errGeneric);
              this._focusError();
            }
            this.dispatchEvent(
              new CustomEvent('ese-back-in-stock:error', {
                bubbles: true,
                detail: { variantId: variantId, status: 0, stage: 'bis' },
              })
            );
            return;
          }
          if (this._bisController === bisController) this._bisController = null;
          classification = response.ok ? null : await classifyBisError(response);
        }
      }

      if (!response.ok) {
        // Never log or render Klaviyo's error detail text — status + code only.
        console.warn('[ese-back-in-stock]', response.status, classification.code);

        if (this._isCurrent(token)) {
          this._setBusy(false);

          if (response.status === 429) {
            this._setError(this.dataset.errRate);
            this._focusError();
          } else if ((response.status === 400 || response.status === 404) && classification.pointerMentionsVariant) {
            this._setError(this.dataset.errUnavailable);
            this._focusError();
          } else if (response.status === 400 && classification.detailMentionsEmail) {
            this._emailInput.setAttribute('aria-invalid', 'true');
            this._setError(this.dataset.errEmail);
            this._emailInput.focus();
          } else {
            this._setError(this.dataset.errGeneric);
            this._focusError();
          }
        }

        this.dispatchEvent(
          new CustomEvent('ese-back-in-stock:error', {
            bubbles: true,
            detail: { variantId: variantId, status: response.status, stage: 'bis' },
          })
        );
        return;
      }

      // The transactional back-in-stock request already succeeded by this point, and the list
      // opt-in above (if any) ran to completion regardless of the dialog — report the real outcome
      // even if the shopper closed the dialog while either request was still in flight. UI updates
      // are still skipped once we're no longer current (element gone/dialog closed).
      //
      // Three distinct outcomes, kept separate so the shopper is never told something untrue:
      //   emailListFailed - email marketing was ticked and no list call recorded it.
      //   smsListFailed   - a phone was entered, but the SMS marketing list did NOT record consent
      //                     (the list call failed, or only its email-only retry succeeded).
      //   smsAlertFailed  - the list call DID record the phone, but the restock text could not be
      //                     set up (the back-in-stock SMS attempt was retried email-only). The
      //                     success view only shows this one when smsListFailed is false.
      const emailListFailed = optedIn && listSubscribed === false;
      const smsListFailed = !!smsPhoneForList && !listPhoneRecorded;
      const smsAlertFailed = smsSubscribed === false && listPhoneRecorded;
      if (this._isCurrent(token)) {
        this._setBusy(false);
        this._showSuccess(emailListFailed, smsListFailed, smsAlertFailed);
      }
      this.dispatchEvent(
        new CustomEvent('ese-back-in-stock:success', {
          bubbles: true,
          detail: { variantId: variantId, listSubscribed: listSubscribed, smsSubscribed: smsSubscribed },
        })
      );
    }
  }

  customElements.define('ese-back-in-stock', EseBackInStock);
})();
